/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import { FlexRow, FlexCol, TextField, Checkbox, Stack } from 'sarsaparilla';
import type { FormError, HolderInfo, PermitSelection } from '../../../types/permit';

interface HolderForm {
    errors: Array<FormError>;
    index: number;
    onChangeHandler: (key?: string, value?: string | boolean) => void;
    reservation: {
        holder: HolderInfo;
        permit: PermitSelection;
        quantity: number;
    };
}

export default function HolderForm({
    errors,
    index,
    onChangeHandler,
    reservation,
}: HolderForm) {
    const { holder } = reservation;
    const getErrorText = (key = '') => {
        const error = errors.find((item) => {
            return item.inputName === key;
        });

        return error?.errorText || '';
    };
    const validateEKOPass = (value: string) => {
        return {
            isValid: value.length >= 9 && value.length <= 16,
            formattedValue: null,
            errorText: 'Invalid pass number.',
        };
    };

    return (
        <div className="tp-holder-form">
            <Stack>
                <FlexRow>
                    <FlexCol lg={6}>
                        <TextField
                            id={`first-name-${index}`}
                            label="First Name"
                            value={holder?.first_name}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].holder.first_name`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText('first_name')}
                            isRequired
                        />
                    </FlexCol>
                    <FlexCol lg={6}>
                        <TextField
                            id={`last-name-${index}`}
                            label="Last Name"
                            value={holder?.last_name}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].holder.last_name`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText('last_name')}
                            isRequired
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol lg={6}>
                        <TextField
                            type="email"
                            id={`email-${index}`}
                            label="Email Address"
                            value={holder?.email}
                            onChange={(e) =>
                                onChangeHandler(`[${index}].holder.email`, e.target.value)
                            }
                            errorText={getErrorText('email')}
                            isRequired
                            data-testid={`email-${index}`}
                        />
                    </FlexCol>
                    <FlexCol lg={6}>
                        <TextField
                            type="email"
                            id={`repeat-email-${index}`}
                            label="Re-enter Email Address"
                            value={holder?.repeat_email}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].holder.repeat_email`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText('repeat_email')}
                            isRequired
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol lg={6}>
                        <TextField
                            id={`zip-code-${index}`}
                            value={holder?.zip_code}
                            label="Zip Code"
                            isRequired
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].holder.zip_code`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText('zip_code')}
                        />
                    </FlexCol>
                    <FlexCol xl="auto">
                        <div className="rec-form-item-wrap" />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol lg={6}>
                        <Checkbox
                            id={`confirm-${index}`}
                            isChecked={holder?.confirm}
                            label="I have a fourth grade Every Kid Outdoors pass"
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].holder.confirm`,
                                    e.target?.checked
                                )
                            }
                        />
                    </FlexCol>
                </FlexRow>
                {holder?.confirm && (
                    <FlexRow>
                        <FlexCol lg={6}>
                            <TextField
                                id={`voucher-${index}`}
                                label="Enter your Every Kid Outdoors Voucher, Physical Pass Serial Number, or Digital Pass Reservation Number"
                                value={holder?.voucher}
                                onChange={(e) =>
                                    onChangeHandler(
                                        `[${index}].holder.voucher`,
                                        e.target.value
                                    )
                                }
                                validator={validateEKOPass}
                            />
                        </FlexCol>
                    </FlexRow>
                )}
            </Stack>
        </div>
    );
}
